import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle impersonation token from URL before loading user
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('token');
    if (impersonateToken && window.location.pathname === '/dashboard') {
      localStorage.setItem('tramilex_token', impersonateToken);
      window.history.replaceState({}, '', '/dashboard');
    }

    const token = localStorage.getItem('tramilex_token');
    if (token) {
      api.get('/auth/me')
        .then(res => {
          const userData = res.data;
          userData.id = userData._id || userData.id;
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem('tramilex_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('tramilex_token', res.data.token);
    // Store password temporarily for force-change flow
    if (res.data.must_change_password) {
      sessionStorage.setItem('tramilex_temp_pw', password);
    }
    try {
      const meRes = await api.get('/auth/me');
      const userData = meRes.data;
      userData.id = userData._id || userData.id;
      setUser(userData);
    } catch {
      setUser(res.data);
    }
    return res.data;
  }, []);

  const companyLogin = useCallback(async (cifNif, password) => {
    const res = await api.post('/auth/company-login', { email: cifNif, password });
    localStorage.setItem('tramilex_token', res.data.token);
    setUser(res.data);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.post('/auth/register', data);
    localStorage.setItem('tramilex_token', res.data.token);
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('tramilex_token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, companyLogin, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
